import { create, fromJson, toJson } from "@bufbuild/protobuf";

import {
  GetNotificationSettingsResponseSchema,
  NotificationSettingsSchema,
  PushSubscriptionSchema,
  WscDeletePushSubscriptionResponseSchema,
  WscSendTestPushResponseSchema,
} from "@uic/adapters/slorgproto";
import { wsc } from "@uic/domain/wsc";

/**
 * The status WSC answers when a device's registration cannot be used as it
 * stands: held by another account, or minted under a VAPID pair this server no
 * longer signs with. Both are repaired the same way, by minting a fresh one.
 */
const REGISTRATION_CONFLICT = 409;

/**
 * Whether a rejected request is WSC refusing to work with this registration.
 * @param {unknown} error The rejected value from a WSC request
 * @returns {boolean} True when the registration has to be replaced
 */
export function isRegistrationConflict(error) {
  return error?.response?.status === REGISTRATION_CONFLICT;
}

/**
 * Load everything the notification settings page renders from: the member's
 * digest schedule, the times on offer, and every registered device.
 * @returns {Promise<object>} The notification settings response
 */
export async function getNotificationSettings() {
  const response = await wsc.get("/push/settings");
  return fromJson(GetNotificationSettingsResponseSchema, response.data);
}

/**
 * Save the member's digest schedule.
 * @param {number} digestTime The DigestTime enum value to send the digest at
 * @returns {Promise<object>} The saved settings
 */
export async function updateNotificationSettings(digestTime) {
  const request = create(NotificationSettingsSchema, { digestTime });
  const response = await wsc.put(
    "/push/settings",
    toJson(NotificationSettingsSchema, request),
  );
  return fromJson(NotificationSettingsSchema, response.data);
}

/**
 * Register this device's push subscription.
 * @param {object} subscription Plain subscription data
 * @param {string} subscription.endpoint The push service endpoint
 * @param {string} subscription.publicKey The subscription's p256dh key
 * @param {string} subscription.authSecret The subscription's auth secret
 * @param {string} subscription.timezone The device's IANA timezone name
 * @param {boolean} subscription.enabled Whether this device should be sent to
 * @param {string} subscription.deviceLabel A human-readable name for the device
 * @returns {Promise<object>} The registered subscription
 */
export async function registerSubscription(subscription) {
  const request = create(PushSubscriptionSchema, subscription);
  const response = await wsc.post(
    "/push/subscriptions",
    toJson(PushSubscriptionSchema, request),
  );
  return fromJson(PushSubscriptionSchema, response.data);
}

/**
 * Update a registered device's enabled state, label or timezone.
 * @param {string} id The subscription ID
 * @param {object} subscription The fields to apply
 * @returns {Promise<object>} The updated subscription
 */
export async function updateSubscription(id, subscription) {
  const request = create(PushSubscriptionSchema, subscription);
  const response = await wsc.put(
    `/push/subscriptions/${id}`,
    toJson(PushSubscriptionSchema, request),
  );
  return fromJson(PushSubscriptionSchema, response.data);
}

/**
 * Forget a registered device.
 * @param {string} id The subscription ID
 * @returns {Promise<boolean>} Whether the delete succeeded
 */
export async function deleteSubscription(id) {
  const response = await wsc.delete(`/push/subscriptions/${id}`);
  const proto = fromJson(
    WscDeletePushSubscriptionResponseSchema,
    response.data,
  );
  return proto.ok;
}

/**
 * Send a test notification to a registered device.
 * @param {string} id The subscription ID
 * @returns {Promise<boolean>} Whether the send succeeded
 */
export async function sendTestNotification(id) {
  const response = await wsc.post(`/push/subscriptions/${id}/test`);
  const proto = fromJson(WscSendTestPushResponseSchema, response.data);
  return proto.ok;
}

/**
 * The registered device holding an endpoint, or null when the account has none.
 *
 * The endpoint is a parameter rather than read here, because callers read it at
 * different moments: forgetting a device captures it before dropping the browser
 * subscription, which is what makes it unreadable afterwards.
 * @param {string} endpoint The push service endpoint identifying the device
 * @returns {Promise<object|null>} The registered subscription, or null
 */
export async function findDeviceByEndpoint(endpoint) {
  const { subscriptions } = await getNotificationSettings();

  return subscriptions?.find((sub) => sub.endpoint === endpoint) || null;
}
